import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { throttle } from 'throttle-debounce';

import { openEventChannel } from '../../../actions/socket';
import { userSelector } from '../../../selectors';
import { selectShowErrorPage } from '../../../selectors/errorPageSelector';
import { useAppDispatch } from '../../../types/redux/state';
import { ESocketCloseCustomCode } from '../../../types/socket';
import { useApplicationContext } from '../../../utils/applicationContext';

const SOCKET_CLOSE_TIMEOUT = 30000;
const SOCKET_OPEN_DELAY = 1000;

/**
 * @description
 * Изменение сокет-соединения при переключении между вкладками / окнами
 */
export const useFocusChange = () => {
  const socketDisconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const socketReconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const showErrorPage = useSelector(selectShowErrorPage);
  const { isAuthenticated } = useSelector(userSelector);
  const dispatch = useAppDispatch();
  const {
    custom: { socket },
    config,
  } = useApplicationContext();
  const socketCloseTimeout =
    config.get<number>('frontend-chats.socketCloseTimeoutAfterTabInactive') || SOCKET_CLOSE_TIMEOUT;
  const socketOpenDelay =
    config.get<number>('frontend-chats.socketReconnectTimeoutAfterTabActive') || SOCKET_OPEN_DELAY;

  /**
   * Делаем троттлинг, поскольку функция может срабатывать одновременно и в visiblitychange, и в blur.
   */
  const startSocketDisconnectTimer = useCallback(
    throttle(
      300,
      () => {
        if (socketReconnectTimer.current) {
          clearTimeout(socketReconnectTimer.current);
          socketReconnectTimer.current = null;
        }

        socketDisconnectTimer.current = setTimeout(async () => {
          const isConnectionClosed = socket?.eventChannel.isChannelClosed();

          if (socket && !isConnectionClosed) {
            await socket.eventChannel.closeEventChannel(ESocketCloseCustomCode.CurrentBrowserTabBecomeInactive);
          }
        }, socketCloseTimeout);
      },
      { noTrailing: true },
    ),
    [socket, socketCloseTimeout],
  );

  /**
   * @description
   * В Safari при переключении на вкладку фокус вызывается 2 раза, фиксим это троттлингом.
   */
  const trySocketReconnectIfNeeded = useCallback(
    throttle(
      300,
      () => {
        if (socketDisconnectTimer.current) {
          clearTimeout(socketDisconnectTimer.current);
          socketDisconnectTimer.current = null;
        }

        /**
         * @description
         * Через socketOpenDelay после возвращения на вкладку делаем реконнект.
         * Это исключает кейсы, когда пользователь быстро закрывает вкладки.
         */
        socketReconnectTimer.current = setTimeout(() => {
          if (!showErrorPage && socket?.eventChannel.isChannelClosed()) {
            dispatch(openEventChannel(true));
          }
        }, socketOpenDelay);
      },
      { noTrailing: true },
    ),
    [showErrorPage, socket, socketOpenDelay],
  );

  useEffect(() => {
    return () => {
      if (socketReconnectTimer.current) {
        clearTimeout(socketReconnectTimer.current);
      }
    };
  }, []);

  /**
   * @description
   * Переподключаемся к сокету при активации вкладки.
   * document.addEventListener('visibilitychange') не подходит, поскольку фиксирует видимость вкладки.
   * Если пользователь работает в двух окнах параллельно, то видимая вкладка !== активная
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return () => {};
    }

    window.addEventListener('focus', trySocketReconnectIfNeeded);

    return () => {
      window.removeEventListener('focus', trySocketReconnectIfNeeded);
    };
  }, [isAuthenticated, trySocketReconnectIfNeeded]);

  /**
   * @description
   * При активации другого окна / другой вкладки стартуем таймер отключения от сокета.
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return () => {};
    }

    window.addEventListener('blur', startSocketDisconnectTimer);

    return () => {
      window.removeEventListener('blur', startSocketDisconnectTimer);
    };
  }, [isAuthenticated, startSocketDisconnectTimer]);

  /**
   * @description
   * При переходе на другую вкладку с текущей стартуем таймер отключения от сокета.
   * При возвращении на текущую вкладку пробуем переподключиться к сокету, если соединение было прервано.
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return () => {};
    }

    const visibilityChangeHandler = () => {
      if (document.visibilityState === 'hidden') {
        startSocketDisconnectTimer();
      } else {
        trySocketReconnectIfNeeded();
      }
    };

    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () => {
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
    };
  }, [isAuthenticated, startSocketDisconnectTimer, trySocketReconnectIfNeeded]);
};
